import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

export default function History() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'interview' | 'presentation'>('interview');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    axios.get(`${API_BASE_URL}/api/progress/recent`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => {
      setSessions(r.data.sessions);
      setLoading(false);
    }).catch((e) => { console.log('History error:', e.response?.data); setLoading(false); });
  }, [navigate]);

  const getScoreColor = (score: number) => {
    if (score >= 75) return '#22C55E';
    if (score >= 55) return '#F59E0B';
    return '#EF4444';
  };

  const getReadiness = (score: number) => {
    if (score >= 80) return 'HIRE READY';
    if (score >= 65) return 'DEVELOPING';
    if (score >= 50) return 'NEEDS WORK';
    return 'NOT READY';
  };

  const getVerdict = (score: number) => {
    if (score >= 75) return 'PRESENTATION READY';
    if (score >= 55) return 'NEEDS REFINEMENT';
    return 'NOT READY';
  };

  const dimensions = [
    { key: 'avg_executive_presence', label: 'Presence' },
    { key: 'avg_strategic_clarity', label: 'Clarity' },
    { key: 'avg_structured_thinking', label: 'Structure' },
    { key: 'avg_risk_ownership', label: 'Ownership' },
    { key: 'avg_confidence_markers', label: 'Confidence' },
  ];

  const interviewSessions = sessions.filter(s => s.session_type !== 'presentation');
  const presentationSessions = sessions.filter(s => s.session_type === 'presentation');
  const activeSessions = activeTab === 'interview' ? interviewSessions : presentationSessions;

  const getTrend = (list: any[]) => {
    if (list.length < 2) return null;
    return parseFloat(list[0].avg_overall_score) - parseFloat(list[list.length - 1].avg_overall_score);
  };

  const trend = getTrend(activeSessions);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <p style={styles.brand}>VIRILITER</p>
          <h1 style={styles.title}>Your Progress</h1>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'interview' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('interview')}
          >
            Interview Prep
            {interviewSessions.length > 0 && (
              <span style={styles.tabCount}>{interviewSessions.length}</span>
            )}
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'presentation' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('presentation')}
          >
            Presentation Prep
            {presentationSessions.length > 0 && (
              <span style={styles.tabCount}>{presentationSessions.length}</span>
            )}
          </button>
        </div>

        {/* Trend */}
        {trend !== null && (
          <div style={styles.trendCard}>
            <p style={styles.trendLabel}>
              {activeTab === 'interview' ? 'INTERVIEW TREND' : 'PRESENTATION TREND'}
            </p>
            <p style={{
              ...styles.trendValue,
              color: trend > 0 ? '#22C55E' : trend < 0 ? '#EF4444' : '#F59E0B'
            }}>
              {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend).toFixed(0)} pts
            </p>
            <p style={styles.trendSub}>
              {trend > 0 ? 'You are improving' : trend < 0 ? 'Score has declined' : 'Score is stable'}
            </p>
          </div>
        )}

        {loading ? (
          <p style={styles.empty}>Loading your history...</p>
        ) : activeSessions.length === 0 ? (
          <div style={styles.emptyCard}>
            <p style={styles.emptyTitle}>No {activeTab} sessions yet</p>
            <p style={styles.emptyText}>
              Complete your first {activeTab === 'interview' ? 'interview' : 'presentation'} session to see your progress here.
            </p>
            <button
              style={styles.emptyButton}
              onClick={() => navigate(activeTab === 'interview' ? '/setup' : '/presentation-setup')}
            >
              Start {activeTab === 'interview' ? 'Interview' : 'Presentation'} Prep →
            </button>
          </div>
        ) : (
          <div style={styles.sessions}>
            {activeSessions.map((session, i) => (
              <div key={session.session_id + i} style={{ ...styles.sessionCard, cursor: 'pointer' }} onClick={() => navigate(`/scorecard/${session.session_id}`)}>
                <div style={styles.sessionHeader}>
                  <div style={styles.sessionLeft}>
                    <p style={styles.sessionNum}>SESSION {activeSessions.length - i}</p>
                    <p style={styles.roleTitle}>{session.role_title}</p>
                    <p style={styles.companyName}>{session.company_name}</p>
                  </div>
                  <div style={styles.scoreCircle}>
                    <p style={{ ...styles.scoreNumber, color: getScoreColor(parseFloat(session.avg_overall_score)) }}>
                      {parseFloat(session.avg_overall_score).toFixed(0)}
                    </p>
                    <p style={styles.scoreOutOf}>/100</p>
                  </div>
                </div>

                <div style={{
                  ...styles.readinessTag,
                  borderColor: getScoreColor(parseFloat(session.avg_overall_score))
                }}>
                  <span style={{
                    ...styles.readinessText,
                    color: getScoreColor(parseFloat(session.avg_overall_score))
                  }}>
                    {activeTab === 'interview'
                      ? getReadiness(parseFloat(session.avg_overall_score))
                      : getVerdict(parseFloat(session.avg_overall_score))
                    }
                  </span>
                </div>

                <div style={styles.dimensionsGrid}>
                  {dimensions.map(d => (
                    <div key={d.key} style={styles.dimensionItem}>
                      <div style={styles.dimensionBarBg}>
                        <div style={{
                          ...styles.dimensionBar,
                          width: `${session[d.key]}%`,
                          backgroundColor: getScoreColor(parseFloat(session[d.key]))
                        }} />
                      </div>
                      <div style={styles.dimensionLabel}>
                        <span style={styles.dimensionName}>{d.label}</span>
                        <span style={styles.dimensionScore}>{parseFloat(session[d.key]).toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={styles.bottomButtons}>
          <button
            style={styles.actionButton}
            onClick={() => navigate(activeTab === 'interview' ? '/setup' : '/presentation-setup')}
          >
            + New {activeTab === 'interview' ? 'Interview' : 'Presentation'} Session
          </button>
          <button style={styles.homeButton} onClick={() => navigate('/home')}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', justifyContent: 'center', padding: '40px 24px' },
  card: { width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '20px' },
  header: { alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '8px' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  title: { color: '#F1F5F9', fontSize: '30px', fontWeight: '800', textAlign: 'center', margin: 0 },
  tabs: { display: 'flex', backgroundColor: '#0F172A', borderRadius: '12px', padding: '4px', border: '1px solid #1E293B' },
  tab: { flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: 'transparent', color: '#475569', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' },
  tabActive: { backgroundColor: '#1E293B', color: '#F1F5F9' },
  tabCount: { backgroundColor: '#3B82F6', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px' },
  trendCard: { backgroundColor: '#0F172A', borderRadius: '16px', padding: '20px', border: '1px solid #1E293B', textAlign: 'center' },
  trendLabel: { color: '#3B82F6', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', margin: '0 0 8px 0' },
  trendValue: { fontSize: '36px', fontWeight: '800', margin: '0 0 4px 0' },
  trendSub: { color: '#475569', fontSize: '13px', margin: 0 },
  empty: { color: '#475569', textAlign: 'center', fontSize: '14px' },
  emptyCard: { backgroundColor: '#0F172A', borderRadius: '16px', padding: '32px', border: '1px solid #1E293B', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' },
  emptyTitle: { color: '#F1F5F9', fontSize: '16px', fontWeight: '700', margin: 0 },
  emptyText: { color: '#475569', fontSize: '14px', margin: 0, lineHeight: '1.6' },
  emptyButton: { backgroundColor: '#3B82F6', color: '#fff', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', border: 'none', cursor: 'pointer', marginTop: '8px' },
  sessions: { display: 'flex', flexDirection: 'column', gap: '16px' },
  sessionCard: { backgroundColor: '#0F172A', borderRadius: '16px', padding: '20px', border: '1px solid #1E293B', display: 'flex', flexDirection: 'column', gap: '12px' },
  sessionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  sessionLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  sessionNum: { color: '#3B82F6', fontSize: '9px', letterSpacing: '3px', fontWeight: '700', margin: 0 },
  roleTitle: { color: '#F1F5F9', fontSize: '15px', fontWeight: '700', margin: 0 },
  companyName: { color: '#475569', fontSize: '13px', margin: 0 },
  scoreCircle: { textAlign: 'right' },
  scoreNumber: { fontSize: '32px', fontWeight: '800', margin: 0, lineHeight: 1 },
  scoreOutOf: { color: '#475569', fontSize: '12px', margin: 0 },
  readinessTag: { alignSelf: 'flex-start', border: '1px solid', borderRadius: '20px', padding: '4px 12px' },
  readinessText: { fontSize: '10px', fontWeight: '700', letterSpacing: '2px' },
  dimensionsGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  dimensionItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  dimensionBarBg: { height: '4px', backgroundColor: '#1E293B', borderRadius: '2px', overflow: 'hidden' },
  dimensionBar: { height: '100%', borderRadius: '2px' },
  dimensionLabel: { display: 'flex', justifyContent: 'space-between' },
  dimensionName: { color: '#475569', fontSize: '11px' },
  dimensionScore: { color: '#64748B', fontSize: '11px' },
  bottomButtons: { display: 'flex', flexDirection: 'column', gap: '10px' },
  actionButton: { backgroundColor: '#3B82F6', color: '#ffffff', padding: '18px', borderRadius: '12px', fontSize: '16px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  homeButton: { backgroundColor: 'transparent', color: '#475569', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
};